import React, { useState, useEffect } from 'react';
import { Settings, Save, Heart, Coins } from 'lucide-react';
import Button from '../components/UI/Button';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

interface ListingTypesSettings {
  philanthropic: boolean;
  micro_investment: boolean;
}

const AdminListingTypesSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<ListingTypesSettings>({
    philanthropic: true,
    micro_investment: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchSettings();
  }, [user, navigate]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('tbiz_auth_token');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://tbiz.ir'}/backend/api.php?path=listing-types-settings`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('tbiz_auth_token');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://tbiz.ir'}/backend/api.php?path=listing-types-settings`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ settings }),
        }
      );

      const data = await response.json();
      if (data.success) {
        alert('تنظیمات با موفقیت ذخیره شد');
      } else {
        alert('خطا در ذخیره تنظیمات');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('خطا در ارتباط با سرور');
    }
    setSaving(false);
  };

  const toggleType = (type: keyof ListingTypesSettings) => {
    setSettings((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        در حال بارگذاری...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <Settings size={32} />
            تنظیمات انواع آگهی
          </h1>
          <p className="text-gray-600">
            فعال یا غیرفعال کردن انواع خاص آگهی‌ها
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
          {/* Philanthropic */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-rose-100 rounded-full">
                <Heart size={24} className="text-rose-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  عام‌المنفعه
                </h3>
                <p className="text-sm text-gray-600">
                  جذب سرمایه برای کارهای خیریه
                </p>
              </div>
            </div>
            <button
              onClick={() => toggleType('philanthropic')}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                settings.philanthropic ? 'bg-teal-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                  settings.philanthropic ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Micro Investment */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-full">
                <Coins size={24} className="text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  سرمایه‌گذاری خرد
                </h3>
                <p className="text-sm text-gray-600">
                  جذب سرمایه از عموم مردم
                </p>
              </div>
            </div>
            <button
              onClick={() => toggleType('micro_investment')}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                settings.micro_investment ? 'bg-teal-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                  settings.micro_investment ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>توجه:</strong> با غیرفعال کردن هر نوع آگهی، آن گزینه در
              صفحه "چه هدفی دارید؟" نمایش داده نخواهد شد و کاربران نمی‌توانند
              آگهی از آن نوع ایجاد کنند.
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <Button
              onClick={saveSettings}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700 flex items-center gap-2"
            >
              <Save size={18} />
              {saving ? 'در حال ذخیره...' : 'ذخیره تنظیمات'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminListingTypesSettingsPage;
